import { lazy, Suspense, useEffect, type ReactNode } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router';
import { BusinessProvider, useBusinessQuery, useSession } from '@/lib/session.tsx';
import { ApiError } from '@/lib/api.ts';
import { Shell, MoreScreen, Logo } from './Shell.tsx';
import { Button } from '@/ui/Button.tsx';
import { UpdatePrompt } from './UpdatePrompt.tsx';

const Login = lazy(() => import('@/screens/auth/Login.tsx'));
const Join = lazy(() => import('@/screens/auth/Join.tsx'));
const Setup = lazy(() => import('@/screens/setup/Setup.tsx'));
const Home = lazy(() => import('@/screens/home/Home.tsx'));
const SalesList = lazy(() => import('@/screens/sales/SalesList.tsx'));
const SaleForm = lazy(() => import('@/screens/sales/SaleForm.tsx'));
const SaleDetail = lazy(() => import('@/screens/sales/SaleDetail.tsx'));
const PartiesList = lazy(() => import('@/screens/parties/PartiesList.tsx'));
const PartyForm = lazy(() => import('@/screens/parties/PartyForm.tsx'));
const PartyDetail = lazy(() => import('@/screens/parties/PartyDetail.tsx'));
const Statement = lazy(() => import('@/screens/parties/Statement.tsx'));
const MoneyHome = lazy(() => import('@/screens/money/MoneyHome.tsx'));
const PaymentForm = lazy(() => import('@/screens/money/PaymentForm.tsx'));
const TransferForm = lazy(() => import('@/screens/money/TransferForm.tsx'));
const PaymentDetail = lazy(() => import('@/screens/money/PaymentDetail.tsx'));
const AccountDetail = lazy(() => import('@/screens/money/AccountDetail.tsx'));
const PurchasesList = lazy(() => import('@/screens/purchases/PurchasesList.tsx'));
const PurchaseForm = lazy(() => import('@/screens/purchases/PurchaseForm.tsx'));
const PurchaseDetail = lazy(() => import('@/screens/purchases/PurchaseDetail.tsx'));
const ItemsList = lazy(() => import('@/screens/items/ItemsList.tsx'));
const ItemForm = lazy(() => import('@/screens/items/ItemForm.tsx'));
const ItemDetail = lazy(() => import('@/screens/items/ItemDetail.tsx'));
const StockList = lazy(() => import('@/screens/stock/StockList.tsx'));
const StockCount = lazy(() => import('@/screens/stock/StockCount.tsx'));
const FleetList = lazy(() => import('@/screens/fleet/FleetList.tsx'));
const VehicleForm = lazy(() => import('@/screens/fleet/VehicleForm.tsx'));
const VehicleDetail = lazy(() => import('@/screens/fleet/VehicleDetail.tsx'));
const ReportsHome = lazy(() => import('@/screens/reports/ReportsHome.tsx'));
const Pnl = lazy(() => import('@/screens/reports/Pnl.tsx'));
const Daybook = lazy(() => import('@/screens/reports/Daybook.tsx'));
const Dues = lazy(() => import('@/screens/reports/Dues.tsx'));
const GstReport = lazy(() => import('@/screens/reports/GstReport.tsx'));
const CashFlow = lazy(() => import('@/screens/reports/CashFlow.tsx'));
const ItemSales = lazy(() => import('@/screens/reports/ItemSales.tsx'));
const SalesBreakdown = lazy(() => import('@/screens/reports/SalesBreakdown.tsx'));
const MoneyBreakdown = lazy(() => import('@/screens/reports/MoneyBreakdown.tsx'));
const PnlLines = lazy(() => import('@/screens/reports/PnlLines.tsx'));
const VehicleProfit = lazy(() => import('@/screens/reports/VehicleProfit.tsx'));
const DriverCash = lazy(() => import('@/screens/money/DriverCash.tsx'));
const ConfirmPayments = lazy(() => import('@/screens/money/ConfirmPayments.tsx'));
const TransferDetail = lazy(() => import('@/screens/money/TransferDetail.tsx'));
const Settings = lazy(() => import('@/screens/settings/Settings.tsx'));
const BusinessSettings = lazy(() => import('@/screens/settings/BusinessSettings.tsx'));
const Team = lazy(() => import('@/screens/settings/Team.tsx'));
const RoleEditor = lazy(() => import('@/screens/settings/RoleEditor.tsx'));
const Categories = lazy(() => import('@/screens/settings/Categories.tsx'));
const Account = lazy(() => import('@/screens/settings/Account.tsx'));

export function App() {
  return (
    <>
      <UpdatePrompt />
      <AppRoutes />
    </>
  );
}

function AppRoutes() {
  const { me, loading } = useSession();
  const location = useLocation();

  if (loading) return <Splash />;

  if (!me) {
    return (
      <Suspense fallback={<Splash />}>
        <Routes>
          <Route path="/join/:token" element={<Join />} />
          <Route path="/login" element={<Login />} />
          <Route path="*" element={<Navigate to="/login" replace state={{ from: location.pathname }} />} />
        </Routes>
      </Suspense>
    );
  }

  if (me.businesses.length === 0) {
    return (
      <Suspense fallback={<Splash />}>
        <Routes>
          <Route path="/join/:token" element={<Join />} />
          <Route path="/setup" element={<Setup />} />
          <Route path="*" element={<Navigate to="/setup" replace />} />
        </Routes>
      </Suspense>
    );
  }

  return <BusinessApp />;
}

function BusinessApp() {
  const q = useBusinessQuery();
  const { refreshMe, selectBusiness } = useSession();
  if (q.isPending) return <Splash />;
  if (q.isError || !q.data) {
    // Removed from this business while the app was open: move on instead of retrying forever.
    const removed = q.error instanceof ApiError && q.error.status === 403;
    return (
      <div className="flex min-h-dvh flex-col items-center justify-center gap-4 px-6 text-center">
        <Logo size={48} />
        <p className="max-w-[280px] text-[15px] text-ink-2">
          {removed ? 'You’re no longer part of this business. Ask the owner if this is a mistake.' : q.error?.message ?? 'Couldn’t open your business.'}
        </p>
        {removed ? (
          <Button onClick={async () => { selectBusiness(null); await refreshMe(); }}>Continue</Button>
        ) : (
          <Button onClick={() => q.refetch()}>Try again</Button>
        )}
      </div>
    );
  }
  return (
    <BusinessProvider data={q.data} refresh={q.refetch}>
      <ScrollToTop />
      <Suspense fallback={<RouteFallback />}>
        <Routes>
          <Route path="/join/:token" element={<Join />} />
          <Route path="/setup" element={<Setup />} />
          <Route path="/login" element={<Navigate to="/" replace />} />
          <Route element={<Shell />}>
            <Route index element={<Home />} />
            <Route path="more" element={<MoreScreen />} />
            <Route path="sales" element={<SalesList />} />
            <Route path="sales/new" element={<SaleForm />} />
            <Route path="sales/:id" element={<SaleDetail />} />
            <Route path="sales/:id/edit" element={<SaleForm />} />
            <Route path="parties" element={<PartiesList />} />
            <Route path="parties/new" element={<PartyForm />} />
            <Route path="parties/:id" element={<PartyDetail />} />
            <Route path="parties/:id/edit" element={<PartyForm />} />
            <Route path="parties/:id/statement" element={<Statement />} />
            <Route path="money" element={<MoneyHome />} />
            <Route path="money/in" element={<PaymentForm direction="in" />} />
            <Route path="money/out" element={<PaymentForm direction="out" />} />
            <Route path="money/transfer" element={<TransferForm />} />
            <Route path="money/payments/:id" element={<PaymentDetail />} />
            <Route path="money/payments/:id/edit" element={<PaymentForm direction="in" />} />
            <Route path="money/accounts/:id" element={<AccountDetail />} />
            <Route path="money/drivers" element={<DriverCash />} />
            <Route path="money/confirm" element={<ConfirmPayments />} />
            <Route path="money/transfers/:id" element={<TransferDetail />} />
            <Route path="purchases" element={<PurchasesList />} />
            <Route path="purchases/new" element={<PurchaseForm />} />
            <Route path="purchases/:id" element={<PurchaseDetail />} />
            <Route path="purchases/:id/edit" element={<PurchaseForm />} />
            <Route path="items" element={<ItemsList />} />
            <Route path="items/new" element={<ItemForm />} />
            <Route path="items/:id" element={<ItemDetail />} />
            <Route path="items/:id/edit" element={<ItemForm />} />
            <Route path="stock" element={<StockList />} />
            <Route path="stock/count" element={<StockCount />} />
            <Route path="fleet" element={<FleetList />} />
            <Route path="fleet/new" element={<VehicleForm />} />
            <Route path="fleet/:id" element={<VehicleDetail />} />
            <Route path="fleet/:id/edit" element={<VehicleForm />} />
            <Route path="reports" element={<ReportsHome />} />
            <Route path="reports/pnl" element={<Pnl />} />
            <Route path="reports/daybook" element={<Daybook />} />
            <Route path="reports/receivables" element={<Dues kind="customer" />} />
            <Route path="reports/payables" element={<Dues kind="vendor" />} />
            <Route path="reports/gst" element={<GstReport />} />
            <Route path="reports/cash-flow" element={<CashFlow />} />
            <Route path="reports/items" element={<ItemSales />} />
            <Route path="reports/sales" element={<SalesBreakdown />} />
            <Route path="reports/collected" element={<MoneyBreakdown key="in" dir="in" />} />
            <Route path="reports/paid-out" element={<MoneyBreakdown key="out" dir="out" />} />
            <Route path="reports/pnl/accounts/:id" element={<PnlLines />} />
            <Route path="reports/vehicles" element={<VehicleProfit />} />
            <Route path="settings" element={<Settings />} />
            <Route path="settings/business" element={<BusinessSettings />} />
            <Route path="settings/team" element={<Team />} />
            <Route path="settings/roles/:id" element={<RoleEditor />} />
            <Route path="settings/categories" element={<Categories />} />
            <Route path="settings/account" element={<Account />} />
            <Route path="*" element={<NotFound />} />
          </Route>
        </Routes>
      </Suspense>
    </BusinessProvider>
  );
}

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

export function Splash() {
  return (
    <div className="flex min-h-dvh items-center justify-center">
      <div className="animate-pop"><Logo size={56} /></div>
    </div>
  );
}

function RouteFallback() {
  return (
    <div className="mx-auto max-w-[640px] px-4 pt-20">
      <div className="skeleton mb-4 h-8 w-1/2 rounded-lg" />
      <div className="skeleton mb-3 h-32 rounded-[20px]" />
      <div className="skeleton h-48 rounded-[20px]" />
    </div>
  );
}

function NotFound(): ReactNode {
  return (
    <div className="mx-auto flex max-w-[640px] flex-col items-center px-6 pt-24 text-center">
      <p className="text-[48px] font-bold tracking-[-0.04em] text-ink-3">404</p>
      <p className="mt-2 text-[16px] text-ink-2">This page doesn’t exist.</p>
      <Button className="mt-6" onClick={() => window.history.back()}>Go back</Button>
    </div>
  );
}
